/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import {
    signIn,
    confirmSignIn,
    signOut,
    fetchAuthSession,
    resetPassword as amplifyResetPassword,
    confirmResetPassword,
} from "aws-amplify/auth";

// Amplify v6. The method signatures and return shapes below are the contract the
// rest of the app depends on (12 call sites in apis/api.js, the four auth screens,
// AuthContext and ProtectedRoute), so they are deliberately unchanged from v5 —
// including the inconsistency where `login` returns { status } and the others
// return { success }.
// Plain methods rather than arrow class fields: the project's eslint config is
// ecmaVersion 12, so the v5 version of this file could not be parsed at all. Every
// call site invokes through an instance, so `this` binding is unaffected.
export default class Auth {
    async checkAuth() {
        try {
            // v5 read `currentUserCredentials().authenticated`. v6 has no such field:
            // fetchAuthSession() resolves for signed-out visitors too (guest access is
            // enabled), just with `tokens` undefined. Checking the id token is what
            // actually distinguishes a signed-in user here.
            const { tokens } = await fetchAuthSession();
            return !!tokens?.idToken;
        } catch (error) {
            console.error("Error checking auth", error);
            return false;
        }
    }

    // v5's currentSession() threw when signed out. v6's fetchAuthSession() resolves
    // with tokens undefined instead, which would put the literal string "undefined"
    // into the Authorization header and send api.js into its refresh-and-retry path —
    // unbounded in getPersistedData/getGroups, which have no retry counter. So keep
    // the v5 contract and throw.
    async getToken() {
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken?.toString();

        if (!idToken) {
            throw new Error("No current user session");
        }
        return idToken;
    }

    async getAccessToken() {
        const { tokens } = await fetchAuthSession();
        const accessToken = tokens?.accessToken?.toString();

        if (!accessToken) {
            throw new Error("No current user session");
        }
        return accessToken;
    }

    async login(username, password) {
        try {
            const { nextStep } = await signIn({ username, password });

            const status =
                nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
                    ? "new-password-required"
                    : "success";
            return { status, errorMessage: undefined };
        } catch (error) {
            // v6 throws when a session already exists rather than returning the user,
            // which would otherwise wedge the form in an already-signed-in tab.
            if (error?.name === "UserAlreadyAuthenticatedException") {
                return { status: "success", errorMessage: undefined };
            }

            console.error("signin error", error);
            return {
                status: undefined,
                errorMessage:
                    error instanceof Error ? error.message : "Unknown error logging in",
            };
        }
    }

    async completePassword(newPass) {
        try {
            // v5 needed the user object captured by login() in `tempCurrentUser`, which
            // meant this only worked when both screens shared one Auth instance and
            // broke on a reload in between. v6 tracks the challenge internally.
            await confirmSignIn({ challengeResponse: newPass });
            return { success: true, errorMessage: undefined };
        } catch (error) {
            console.error("Complete password error", error);
            return {
                success: false,
                errorMessage:
                    error instanceof Error
                        ? error.message
                        : "Server Error, try again later",
            };
        }
    }

    async resetPassword(username) {
        try {
            await amplifyResetPassword({ username });
            return { success: true };
        } catch (error) {
            return {
                success: false,
                errorMessage:
                    error instanceof Error
                        ? error.message
                        : "Server Error, try again later",
            };
        }
    }

    async completeReset(username, code, newPass) {
        try {
            await confirmResetPassword({
                username,
                confirmationCode: code,
                newPassword: newPass,
            });
            return { success: true, errorMessage: undefined };
        } catch (error) {
            console.error("Complete reset password error", error);
            return {
                success: false,
                errorMessage:
                    error instanceof Error
                        ? error.message
                        : "Server Error, try again later",
            };
        }
    }

    // Sign-out was never wired up under v5 — the navbar just cleared localStorage.
    // v6 caches tokens in memory as well, so the session has to be torn down properly.
    async logout() {
        try {
            await signOut();
            return { success: true, errorMessage: undefined };
        } catch (error) {
            console.error("Sign out error", error);
            return {
                success: false,
                errorMessage:
                    error instanceof Error ? error.message : "Server Error, try again later",
            };
        }
    }
}
